import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentProject, setModal } from '../../redux/application-slice'
import { ProjectDocumentMetadata } from '../../shared/interfaces/project/document/document.interface'
import DocumentListbox from '../document-listbox/document-listbox'
import { skipToken } from '@reduxjs/toolkit/query'
import {
  useGetDocumentsListByProjectQuery,
  useMarkDocumentAsScDocumentMutation,
  useMarkDocumentAsScTemplateMutation,
} from '../../redux/api-slice'
import { toast } from 'react-toastify'
import { selectCurrentPage } from '../../redux/viewer-slice'
import { useNavigate } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

interface SupplementaryConditionsModalProps {
  open: boolean
}

const SupplementaryConditionsModal: React.FC<
  SupplementaryConditionsModalProps
> = ({ open }) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState<boolean>(open)
  const dispatch = useDispatch()
  const currentProject = useSelector(selectCurrentProject)
  const currentPage = useSelector(selectCurrentPage)
  const [selectedTemplateDocument, setSelectedTemplateDocument] =
    useState<ProjectDocumentMetadata | null>(null)
  const [
    selectedSupplementaryConditionsDocument,
    setSelectedSupplementaryConditionsDocument,
  ] = useState<ProjectDocumentMetadata | null>(null)

  const { currentData: documents } = useGetDocumentsListByProjectQuery(
    currentProject ? { projectId: currentProject?.id } : skipToken
  )
  const [markAsScTemplate] = useMarkDocumentAsScTemplateMutation()
  const [markAsScDocument] = useMarkDocumentAsScDocumentMutation()

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const onNavigateToSupplementaryConditions = useCallback(() => {
    if (!selectedSupplementaryConditionsDocument?.uuid) {
      return
    }
    navigate(
      `${currentProject?.uuid}/supplementary-conditions/${selectedSupplementaryConditionsDocument?.uuid}?page=${currentPage}`
    )
  }, [
    currentPage,
    currentProject?.uuid,
    navigate,
    selectedSupplementaryConditionsDocument?.uuid,
  ])

  const onClose = useCallback(() => {
    dispatch(setModal(null))
    setIsOpen(false)
    setSelectedTemplateDocument(null)
    setSelectedSupplementaryConditionsDocument(null)
  }, [dispatch])

  const onRunAnalysis = useCallback(async () => {
    if (
      !selectedTemplateDocument?.id ||
      !selectedSupplementaryConditionsDocument?.id
    ) {
      toast.error(
        'Please select a template and supplementary conditions document'
      )
      return
    }
    await markAsScTemplate(selectedTemplateDocument.id).unwrap()
    await markAsScDocument(selectedSupplementaryConditionsDocument.id).unwrap()
    onClose()
    toast.success(
      <div>
        <div>Supplementary conditions analysis started</div>
        <div>
          <button
            className="flex items-center gap-2 rounded-md text-sm hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={onNavigateToSupplementaryConditions}
          >
            View analysis
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }, [
    selectedTemplateDocument?.id,
    selectedSupplementaryConditionsDocument?.id,
    markAsScTemplate,
    markAsScDocument,
    onClose,
    onNavigateToSupplementaryConditions,
  ])

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} as="div" className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div>
                  <div className="mt-3 text-left sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Analyze supplementary conditions
                    </Dialog.Title>
                    <div className="mt-4 space-y-6">
                      <div className="rounded-lg bg-gray-50 p-4">
                        <h4 className="text-sm font-medium text-gray-900">
                          Template Contract
                        </h4>
                        <p className="mt-1 text-sm text-gray-500">
                          Select the base contract template to compare against.
                        </p>
                        <div className="mt-3">
                          <DocumentListbox
                            noDefault
                            documents={documents}
                            selectedDocument={selectedTemplateDocument}
                            setSelectedDocument={setSelectedTemplateDocument}
                          />
                        </div>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4">
                        <h4 className="text-sm font-medium text-gray-900">
                          Supplementary Conditions
                        </h4>
                        <p className="mt-1 text-sm text-gray-500">
                          Select the supplementary conditions document to
                          analyze.
                        </p>
                        <div className="mt-3">
                          <DocumentListbox
                            noDefault
                            documents={documents}
                            selectedDocument={
                              selectedSupplementaryConditionsDocument
                            }
                            setSelectedDocument={
                              setSelectedSupplementaryConditionsDocument
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${
                      !selectedTemplateDocument ||
                      !selectedSupplementaryConditionsDocument
                        ? 'cursor-not-allowed opacity-50'
                        : ''
                    }`}
                    onClick={onRunAnalysis}
                    disabled={
                      !selectedTemplateDocument ||
                      !selectedSupplementaryConditionsDocument
                    }
                  >
                    Run analysis
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default SupplementaryConditionsModal
